<script setup lang="ts">
// Sign-up shell — a two-column split:
//   left  : brand header, the centred flow card (RouterView), legal footer
//   right : full-bleed showcase carousel (desktop only)
//
// The card itself is owned by the routed container, not by this layout, so
// each sign-up step controls its own height and the shell just centres it.

import { useTemplateRef } from 'vue'
import { RouterView } from 'vue-router'
import Box from '@uiKit/elements/Box.vue'
import { AuthShowcaseCarousel, AuthTopHeader } from '@modules/auth'
import AuthFooter from './AuthFooter.vue'
import { useScrollTopOnRouteChange } from '@shared/composables/useScrollTopOnRouteChange'

const bodyRef = useTemplateRef<InstanceType<typeof Box>>('body')
useScrollTopOnRouteChange(() => bodyRef.value?.$el)
</script>

<template>
  <Box is="main" class="signup-shell">
    <Box class="signup-shell__left">
      <AuthTopHeader />
      <Box ref="body" class="signup-shell__body">
        <RouterView />
      </Box>
      <AuthFooter />
    </Box>

    <Box class="signup-shell__right">
      <AuthShowcaseCarousel />
    </Box>
  </Box>
</template>

<style scoped>
.signup-shell {
  display: flex;
  align-items: stretch;
  /* Fill #app (html/body/#app are height: 100%). `100dvh` + max-height: 100dvh
     capped the shell short of the Windows canvas and left empty space under
     the footer / right panel. */
  height: 100%;
  max-height: 100%;
  overflow: hidden;
  background: var(--p-surface-50);

  /* ── Vertical density scale ──────────────────────────────────────────
     Every vertical rhythm value in the pre-auth surface — this shell, the
     flow cards, the step bodies — derives from ONE dial, and that dial reads
     the VIEWPORT HEIGHT rather than a width breakpoint.

     `--auth-fit` ramps 0 → 300px as the viewport grows from 700px tall to
     1000px tall, and pins at both ends. Each token below is then a straight
     linear interpolation on it: `min + fit x slope`, where the slope is
     (design value - floor) / 300. So every token holds its Figma value from
     ~1000px of viewport height (1080p, MacBook, 1440x900) and compresses
     CONTINUOUSLY below that — a window dragged shorter adapts on every pixel
     instead of snapping at a breakpoint. The floors are the tightest values
     that keep the form readable and its targets tappable.

     Type sizes and button heights are deliberately NOT on this scale: only
     the space between things gives way.

     Custom properties inherit, so the cards and steps read these without the
     layout having to reach into them. */
  --auth-fit: clamp(0px, calc(100dvh - 700px), 300px);

  --auth-header-h: calc(3.625rem + var(--auth-fit) * 0.0733); /* 58 → 80px */
  --auth-footer-pad-y: calc(0.3125rem + var(--auth-fit) * 0.0233); /* 5 → 12px */
  --auth-shell-pad-y: calc(0.5rem + var(--auth-fit) * 0.08); /* 8 → 32px */
  --auth-shell-pad-x: clamp(1rem, 2.4vw, 1.5rem);
  --auth-card-pad: calc(1rem + var(--auth-fit) * 0.0467); /* 16 → 30px */
  --auth-card-gap: calc(0.875rem + var(--auth-fit) * 0.0333); /* 14 → 24px */
  --auth-block-gap: calc(1rem + var(--auth-fit) * 0.0533); /* 16 → 32px */
  --auth-title-gap: calc(0.375rem + var(--auth-fit) * 0.02); /* 6 → 12px */
  --auth-field-gap: calc(0.625rem + var(--auth-fit) * 0.0467); /* 10 → 24px */
  --auth-label-gap: calc(0.3125rem + var(--auth-fit) * 0.01); /* 5 → 8px */
  --auth-control-h: calc(2.375rem + var(--auth-fit) * 0.0133); /* 38 → 42px */
  /* Inset the showcase panel floats inside; also feeds its width maths. */
  --auth-panel-inset: calc(0.625rem + var(--auth-fit) * 0.0267); /* 10 → 18px */
}

.signup-shell__left {
  /* Takes whatever the panel leaves — ~786px at a 1512px frame, against
     Figma's 772px. */
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
  height: 100%;
  align-self: stretch;
  /* header · centred form · footer. The middle row absorbs the slack so the
     card sits centred against the panel, matching the board. */
  display: grid;
  grid-template-rows: auto minmax(0, 1fr) auto;
}

.signup-shell__body {
  /* Column + `margin-block: auto` on the card, NOT `justify-content: center`.
     A centred flex item that overflows splits its overflow above and below,
     but scrollTop clamps at 0 — so the top half becomes permanently
     unreachable. Auto margins centre when there is room and collapse to zero
     when there isn't, so the card is centred in the MAIN area (never against
     the whole viewport) and can never be pushed under the footer. */
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  min-width: 0;
  min-height: 0;
  padding: var(--auth-shell-pad-y) var(--auth-shell-pad-x);
  box-sizing: border-box;
  /* THE scroll container for the pre-auth surface. `html/body/#app` are
     `overflow: hidden` app-wide, so something between the fixed header and the
     footer has to take the overflow — and this column is the right place for
     it rather than each card scrolling inside its own chrome. A card that
     scrolls internally reads as clipped (its own padding and trust strip stay
     frozen while the fields move under them); a card that stays whole and
     lets the COLUMN scroll reads as a normal page. Cards that opt into the
     older behaviour still clamp themselves with their own `max-height: 100%` —
     the clamp deliberately is NOT applied to every child from here. */
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-width: thin;
  scrollbar-color: var(--p-surface-300) transparent;
}

.signup-shell__body::-webkit-scrollbar {
  width: 6px;
}

.signup-shell__body::-webkit-scrollbar-thumb {
  background: var(--p-surface-300);
  border-radius: 3px;
}

.signup-shell__body > * {
  margin-block: auto;
  flex-shrink: 0;
  min-height: 0;
}

.signup-shell__right {
  /* Width FOLLOWS the panel's height, which keeps the board's 708:945 portrait
     shape and is what keeps the whole photograph visible: filling a fixed
     width share instead made the box near-square, and `cover` then zoomed into
     the middle of a portrait photo, losing the sky and the top of the tower.
     At the 1512x982 board this resolves to ~708px, matching Figma.

     The column carries the ratio maths. An `aspect-ratio` panel with
     `width: auto` has no intrinsic width, so a content-sized flex column
     collapses it to zero — the width has to be stated here instead.
       panel height = 100dvh - 2x the vertical inset
       panel width  = that x 0.7492 (708/945), + the right inset
     The clamp bounds it on WIDTH: the 20rem floor stops a very short window
     squeezing the panel into a letterbox strip, the 52% cap stops a very tall
     one squeezing the form column. */
  flex: 0 0 auto;
  width: clamp(20rem, calc((100dvh - 2 * var(--auth-panel-inset)) * 0.7492 + 1.5rem), 52%);
  min-width: 0;
  min-height: 0;
  height: 100%;
  align-self: stretch;
  display: flex;
  align-items: stretch;
  justify-content: flex-end;
  /* Insets from the design board, so the panel floats inside the page
     background rather than bleeding to the viewport edge. */
  padding: var(--auth-panel-inset) 1.5rem var(--auth-panel-inset) 0;
  box-sizing: border-box;
}

/* Below the tablet bound the showcase panel is dropped entirely — the flow
   card carries its own trust strip, which matters more on a phone. The shell
   stays locked to the window (document scroll is closed app-wide); the form
   column above is what scrolls, and only when a card genuinely outgrows it. */
@media (max-width: 992px) {
  .signup-shell {
    flex-direction: column;
  }

  .signup-shell__left {
    flex: 1 1 auto;
    width: 100%;
  }

  .signup-shell__right {
    display: none;
  }

  .signup-shell__body {
    /* The card is already full-width here, so the auto margins only decide
       whether it sits centred or hugs the header. Keep them — a short form on
       a tall phone still reads better centred — but drop the side gutter to
       the app's mobile page padding so the fields get the full width. */
    padding: var(--auth-shell-pad-y) 1rem;
  }
}
</style>
